/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { v4 as uuidv4 } from "uuid";
import {
  Clip,
  ClipWithPlaylistTimes,
  WaveformClip,
  TranscriptionClip,
} from "types/Clip";
import { BlockTypes } from "constants/BlockTypes";

type TrimmableClip = WaveformClip | TranscriptionClip;
const isTrimmableClip = (clip: Clip): clip is TrimmableClip =>
  [BlockTypes.WAVEFORM, BlockTypes.TRANSCRIPTION].includes(clip.type);

export function trimBeginning(
  clipToTrim: ClipWithPlaylistTimes & TrimmableClip,
  playlistCutTime: number,
  minimumDuration = 0.0
): Clip {
  const { startTime, endTime, ...clipToTrimWithoutTimes } = clipToTrim;
  if (playlistCutTime <= startTime || playlistCutTime > endTime) {
    return clipToTrimWithoutTimes;
  }

  const duration = clipToTrim.endTime - playlistCutTime;
  const durationWithSnap = duration >= minimumDuration ? duration : 0;

  return {
    ...clipToTrimWithoutTimes,
    id: uuidv4(),
    offset: clipToTrim.duration - durationWithSnap + clipToTrim.offset,
    duration: durationWithSnap,
  };
}

export function trimEnd(
  clipToTrim: ClipWithPlaylistTimes & TrimmableClip,
  playlistCutTime: number,
  minimumDuration = 0.0
): Clip {
  const { startTime, endTime, ...clipToTrimWithoutTimes } = clipToTrim;
  if (playlistCutTime < startTime || playlistCutTime >= endTime) {
    return clipToTrimWithoutTimes;
  }

  const duration = playlistCutTime - clipToTrim.startTime;
  const durationWithSnap = duration >= minimumDuration ? duration : 0;

  return {
    ...clipToTrimWithoutTimes,
    id: uuidv4(),
    offset: clipToTrim.offset,
    duration: durationWithSnap,
  };
}

export function trimEndIfSplittable(
  clipToTrim: ClipWithPlaylistTimes,
  playlistCutTime: number,
  minimumDuration = 0.0
): Clip {
  return isTrimmableClip(clipToTrim)
    ? trimEnd(clipToTrim, playlistCutTime, minimumDuration)
    : clipToTrim;
}

export function trimBeginningIfSplittable(
  clipToTrim: ClipWithPlaylistTimes,
  playlistCutTime: number,
  minimumDuration = 0.0
): Clip {
  return isTrimmableClip(clipToTrim)
    ? trimBeginning(clipToTrim, playlistCutTime, minimumDuration)
    : clipToTrim;
}
